import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/firebaseAuth';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { auth, db } from '../../auth/firebaseConfig';
import { EyeIcon, EyeSlashIcon, CheckIcon } from '@heroicons/react/24/outline';
import { doc, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  getDocumentById,
  updateDocument,
  queryDocuments,
  insertDocument,
  getDocumentByField,
  updateSiteMetrics,
} from '../../utility/firebaseHelpers';
import googleLogoSvg from '../../../src/media/logo/svgs/google.svg';
import { BaseUser } from '../../types/userTypes';
import { UserPlus } from 'lucide-react';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const fromLandlordToolkit = returnUrl?.includes('/landlord-toolkit') || false;
  const [email, setEmail] = useState('');
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [googleSignupPending, setGoogleSignupPending] = useState(false);
  const [googleUserId, setGoogleUserId] = useState<string | null>(null);
  const [googleSignupData, setGoogleSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    marketingConsent: false,
  });

  const validateForm = () => {
    if (!email) {
      setError('Please enter your email address.');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    if (!isForgotPassword && !password) {
      setError('Please enter your password.');
      return false;
    }
    return true;
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      // Check for deactivated account similar to email login
      const userDoc = await getDocumentByField('users', 'email', userCredential.user.email ?? '');

      // Check if user is banned
      if (userDoc?.status === 'banned') {
        navigate('/account-banned', { replace: true });
        return;
      }

      if (userDoc == null) {
        // New user - show role selection before creating account
        setGoogleUserId(userCredential.user.uid);
        setGoogleSignupData({
          name: userCredential.user.displayName ?? 'User',
          email: userCredential.user.email ?? '',
          phone: userCredential.user.phoneNumber ?? '',
          role: fromLandlordToolkit ? 'landlord' : '',
          marketingConsent: false,
        });
        setGoogleSignupPending(true);
        setIsLoading(false);
        return;
      }

      if (userDoc?.isDeactivated) {
        // Handle reactivation logic (same as in email login)
        await updateDocument('users', userCredential.user.uid, {
          isDeactivated: false,
          reactivatedAt: serverTimestamp(),
          status: 'active',
        });
        toast.success('Your account has been successfully reactivated!');
      }

      let redirectTo = returnUrl || location.state?.from?.pathname || '/browse';
      if (userDoc?.role === 'fsbo' && !returnUrl) {
        redirectTo = '/dashboard/fsbo';
      }
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setError('Failed to sign in with Google. Please try again.');
      console.error(error);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    if (isForgotPassword) {
      try {
        await sendPasswordResetEmail(auth, email);
        setResetEmailSent(true);
      } catch (error) {
        setError('Failed to send password reset email. Please try again.');
        console.error(error);
      }
    } else {
      try {
        // First authenticate the user
        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // Check if account is deactivated
        const userDoc = await getDocumentById('users', userCredential.user.uid);

        // Check if user is banned - redirect immediately
        if (userDoc?.status === 'banned') {
          navigate('/account-banned', { replace: true });
          return;
        }

        if (userDoc?.isDeactivated) {
          // Reactivate the account
          await updateDocument('users', userCredential.user.uid, {
            isDeactivated: false,
            reactivatedAt: serverTimestamp(),
            status: 'active',
          });

          // Also reactivate associated listings
          const listings = await queryDocuments('listings', [
            where('propertyOwnerUid', '==', userCredential.user.uid),
            where('status', '==', 'inactive'),
          ]);

          const batch = writeBatch(db);
          listings.forEach((listing) => {
            const listingRef = doc(db, 'listings', listing.id.toString());
            batch.update(listingRef, {
              status: 'active',
              reactivatedAt: serverTimestamp(),
            });
          });

          await batch.commit();

          // Show success message
          toast.success('Your account has been successfully reactivated!');
        }

        // Navigate to intended destination
        let redirectTo = returnUrl || location.state?.from?.pathname || '/browse';
        if (userDoc?.role === 'fsbo' && !returnUrl) {
          redirectTo = '/dashboard/fsbo';
        }
        navigate(redirectTo, { replace: true });
      } catch (error) {
        setError('Failed to log in. Please check your credentials and try again.');
        console.error(error);
      }
    }

    setIsLoading(false);
  };

  const toggleForgotPassword = () => {
    setIsForgotPassword(!isForgotPassword);
    setError('');
    setResetEmailSent(false);
  };

  const completeGoogleSignup = async () => {
    if (!googleUserId || !googleSignupData.role) {
      setError('Please select how you would like to use RentSmart.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let userDefaultBio = 'I am looking for a place to rent.';
      if (googleSignupData.role === 'landlord') {
        userDefaultBio = 'I have a property available for rent or sale.';
      } else if (googleSignupData.role === 'agent') {
        userDefaultBio = 'I manage rental properties on behalf of landlords.';
      } else if (googleSignupData.role === 'fsbo') {
        userDefaultBio = 'I am selling my home directly (FSBO).';
      }

      // Create user document with role and marketing consent
      const userData: BaseUser = {
        id: googleUserId,
        name: googleSignupData.name,
        email: googleSignupData.email,
        phone: googleSignupData.phone,
        accountVerification: {
          emailVerified: false,
          phoneVerified: false,
          identityVerified: false,
        },
        accountCreationDate: serverTimestamp(),
        bio: userDefaultBio,
        communicationPreferences: {
          emailOffers: googleSignupData.marketingConsent,
          pushNotifications: googleSignupData.marketingConsent,
        },
        listingPreferences: {
          showEmail: false,
          showMobile: false,
        },
        role: googleSignupData.role as 'landlord' | 'renter' | 'agent' | 'fsbo',
        engagementEmailsEnabled: true,
        marketingConsent: googleSignupData.marketingConsent,
        ...(googleSignupData.role === 'fsbo' && {
          fsboProfiles: {
            progress: {
              hasSolicitor: false,
              hasBER: false,
              hasTitleDeeds: false,
              hasPlanningDocs: false,
              hasComplianceCerts: false,
              hasLPTClearance: false,
              hasMortgageInfo: false,
              hasManagementInfo: false,
              hasFixturesList: false,
              hasKeysReady: false,
            },
            progressHistory: {},
          },
        }),
      };

      await insertDocument('users', userData, googleUserId);

      // Update site metrics
      const metricsUpdate: Record<string, number> = {
        totalUsers: 1,
      };
      if (googleSignupData.role === 'renter') {
        metricsUpdate.totalRenters = 1;
      } else if (googleSignupData.role === 'landlord') {
        metricsUpdate.totalLandlords = 1;
      } else if (googleSignupData.role === 'agent') {
        metricsUpdate.totalPropertyAgents = 1;
      } else if (googleSignupData.role === 'fsbo') {
        metricsUpdate.totalFsboSellers = 1;
      }
      await updateSiteMetrics(metricsUpdate);

      // Navigate based on role and context
      let redirectTo = returnUrl || location.state?.from?.pathname || '/browse';
      if (googleSignupData.role === 'fsbo') {
        redirectTo = '/dashboard/fsbo';
      } else if (googleSignupData.role === 'landlord' || googleSignupData.role === 'agent') {
        redirectTo = '/onboarding';
      }

      setGoogleSignupPending(false);
      setGoogleUserId(null);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setError('Failed to complete signup. Please try again.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && !googleSignupPending) {
      // Fetch userDoc to check for fsbo role
      (async () => {
        let redirectTo = returnUrl || location.state?.from?.pathname || '/browse';
        try {
          const userDoc = await getDocumentById('users', currentUser.uid);
          if (userDoc?.role === 'fsbo' && !returnUrl) {
            redirectTo = '/dashboard/fsbo';
          }
        } catch {}
        if (location.pathname !== redirectTo) {
          navigate(redirectTo, { replace: true });
        }
      })();
    }
  }, [currentUser, navigate, location, returnUrl, googleSignupPending]);

  // Show role selection for new Google signups
  if (googleSignupPending) {
    return (
      <div className="w-full max-w-md mx-auto mt-8">
        <div className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
          <div className="text-center mb-6">
            <UserPlus className="h-12 w-12 text-blue-500 mx-auto mb-2" />
            <h2 className="text-3xl font-bold mb-2 text-gray-800">Welcome to RentSmart!</h2>
            <p className="text-center text-gray-600">How would you like to use RentSmart?</p>
          </div>

          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <fieldset className="mb-6">
            <legend className="block text-gray-700 text-sm font-semibold mb-2">
              What would you like to do?*
            </legend>
            <div className="flex flex-col space-y-2">
              <label
                htmlFor="login-role-renter"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${
                  googleSignupData.role === 'renter'
                    ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <input
                  id="login-role-renter"
                  type="radio"
                  name="role"
                  value="renter"
                  checked={googleSignupData.role === 'renter'}
                  onChange={(e) =>
                    setGoogleSignupData((prev) => ({ ...prev, role: e.target.value }))
                  }
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${
                      googleSignupData.role === 'renter'
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {googleSignupData.role === 'renter' && (
                      <CheckIcon className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      I want to rent a property
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Browse active listings, book viewings, and apply easily.
                    </p>
                  </div>
                </div>
              </label>

              <label
                htmlFor="login-role-landlord"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${
                  googleSignupData.role === 'landlord'
                    ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <input
                  id="login-role-landlord"
                  type="radio"
                  name="role"
                  value="landlord"
                  checked={googleSignupData.role === 'landlord'}
                  onChange={(e) =>
                    setGoogleSignupData((prev) => ({ ...prev, role: e.target.value }))
                  }
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${
                      googleSignupData.role === 'landlord'
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {googleSignupData.role === 'landlord' && (
                      <CheckIcon className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      I want to list a property
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Post listings, manage inquiries, and track tenant payments.
                    </p>
                  </div>
                </div>
              </label>

              <label
                htmlFor="login-role-agent"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${
                  googleSignupData.role === 'agent'
                    ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <input
                  id="login-role-agent"
                  type="radio"
                  name="role"
                  value="agent"
                  checked={googleSignupData.role === 'agent'}
                  onChange={(e) =>
                    setGoogleSignupData((prev) => ({ ...prev, role: e.target.value }))
                  }
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${
                      googleSignupData.role === 'agent'
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {googleSignupData.role === 'agent' && (
                      <CheckIcon className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">I'm a property agent</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Manage properties, connect buyers, and track sales.
                    </p>
                  </div>
                </div>
              </label>

              <label
                htmlFor="login-role-fsbo"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${
                  googleSignupData.role === 'fsbo'
                    ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <input
                  id="login-role-fsbo"
                  type="radio"
                  name="role"
                  value="fsbo"
                  checked={googleSignupData.role === 'fsbo'}
                  onChange={(e) =>
                    setGoogleSignupData((prev) => ({ ...prev, role: e.target.value }))
                  }
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${
                      googleSignupData.role === 'fsbo'
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {googleSignupData.role === 'fsbo' && (
                      <CheckIcon className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      I'm selling my home (FSBO)
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Sell directly (For Sale By Owner) with guided steps.
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </fieldset>

          <div className="mb-6 space-y-3">
            <label className="inline-flex items-start">
              <input
                type="checkbox"
                checked={googleSignupData.marketingConsent}
                onChange={(e) =>
                  setGoogleSignupData((prev) => ({
                    ...prev,
                    marketingConsent: e.target.checked,
                  }))
                }
                className="form-checkbox text-blue-500 mt-1"
              />
              <span className="ml-2 text-sm text-gray-700">
                Yes, I'd like to receive exclusive property updates, personalized recommendations,
                and special offers from RentSmart.ie. I can unsubscribe anytime.
              </span>
            </label>
          </div>

          <button
            onClick={completeGoogleSignup}
            disabled={isLoading || !googleSignupData.role}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Completing Signup...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto mt-8">
      <div className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
        {fromLandlordToolkit && !isForgotPassword ? (
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold mb-2 text-gray-800">Welcome Back, Landlord</h2>
            <p className="text-gray-600 font-medium">Access your property management dashboard</p>
          </div>
        ) : (
          <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
            {isForgotPassword ? 'Reset Password' : 'Login to your account'}
          </h2>
        )}

        {/* Landlord-specific quick access benefits */}
        {fromLandlordToolkit && !isForgotPassword && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm font-semibold text-gray-800 mb-2">🚀 Quick Access To:</p>
            <div className="space-y-1.5">
              <div className="flex items-center text-sm text-gray-700">
                <svg
                  className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Rent tracking dashboard & automated reminders</span>
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg
                  className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Tenant management & communication tools</span>
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg
                  className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>RTB & RPZ compliance calculators</span>
              </div>
            </div>
          </div>
        )}

        {/* Social Login Buttons */}
        {!isForgotPassword && (
          <div className="space-y-4 mb-6">
            <button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <img className="h-5 w-5 mr-2" src={googleLogoSvg} alt="Google" />
              Continue with Google
            </button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          {resetEmailSent && (
            <div
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">
                Password reset email sent. Please check your inbox.
              </span>
            </div>
          )}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              type="email"
              placeholder="Email address"
              className="shadow-sm appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {!isForgotPassword && (
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••"
                  className="shadow-sm appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          )}
          {isForgotPassword && (
            <div className="mb-6 text-sm text-gray-600">
              Enter your email address above and click the button below. We'll send you an email
              with instructions to reset your password.
            </div>
          )}
          <div className="flex items-center justify-between mb-6">
            <button
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline flex items-center justify-center"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {isForgotPassword ? 'Sending...' : 'Logging in...'}
                </>
              ) : isForgotPassword ? (
                'Reset Password'
              ) : fromLandlordToolkit ? (
                'Access Dashboard →'
              ) : (
                'Login'
              )}
            </button>
          </div>
        </form>
        <div className="text-center">
          <button
            onClick={toggleForgotPassword}
            className="text-blue-500 hover:text-blue-700 font-semibold"
          >
            {isForgotPassword ? 'Back to Login' : 'Forgot password?'}
          </button>
        </div>

        {/* Landlord-specific trust indicators */}
        {fromLandlordToolkit && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center gap-6 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Secure Login</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">100% Free</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Trusted by 1,000+</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            New here?{' '}
            <Link
              to={returnUrl ? `/signup?returnUrl=${encodeURIComponent(returnUrl)}` : '/signup'}
              className="text-blue-500 hover:text-blue-700 font-semibold"
            >
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
